import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import apiService from '../services/api';
import FundingCharts from './FundingCharts';
import {
  Container,
  Card,
  Eyebrow,
  PageTitle,
  Lead,
  PageHeader,
  CardTitle,
  Button,
  Input,
  Segmented,
  SegmentedItem,
  Notice,
  Loading,
  EmptyState,
  Badge,
  Rate,
  rateTone,
  IconChevronRight,
  IconRefresh,
} from './ui';

const CURRENCIES = ['BTC', 'XAU', 'SOL', 'ATOM', 'ETH', 'DOT', 'TRX'];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    width: 100%;

    > * {
      flex: 1 1 auto;
    }
  }
`;

const DateInput = styled(Input)`
  width: 170px;
  height: 36px;
  font-size: 0.9375rem;
  font-variant-numeric: tabular-nums;
  background: var(--fill);
  border-color: transparent;
`;

const AvailableDates = styled.p`
  font-size: 0.8125rem;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
`;

const Scheduler = styled.details`
  border-top: 1px solid var(--separator);
  padding-top: 14px;

  summary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    cursor: pointer;
    list-style: none;

    &::-webkit-details-marker {
      display: none;
    }

    svg {
      transition: transform 200ms var(--ease-out);
    }

    &:hover {
      color: var(--text);
    }
  }

  &[open] summary svg {
    transform: rotate(90deg);
  }
`;

const SchedulerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  margin-top: 14px;
`;

const SchedulerLabel = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const TimeInputs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-tertiary);
`;

const SchedulerInputShort = styled(Input)`
  width: 64px;
  height: 36px;
  padding: 0 8px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const SchedulerStatus = styled.span`
  font-size: 0.8125rem;
  color: ${props => (props.$tone === 'error' ? 'var(--negative)' : 'var(--positive)')};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const StatCard = styled(Card)`
  padding: 20px 24px;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.022em;
  line-height: 1.2;
  color: var(--text);
  font-variant-numeric: tabular-nums;
`;

const PanelHeader = styled.div`
  padding: 20px 24px 16px;
`;

const DataTable = styled.div`
  overflow-x: auto;
  border-top: 1px solid var(--separator);
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  vertical-align: bottom;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--surface-muted);
  border-bottom: 1px solid var(--separator);
  white-space: nowrap;

  small {
    display: block;
    margin-top: 2px;
    font-weight: 400;
    color: var(--text-tertiary);
  }
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid var(--separator);
  vertical-align: top;
`;

const ExchangeRow = styled.tr`
  &:last-child ${Td} {
    border-bottom: 0;
  }

  &:hover ${Td} {
    background: var(--surface-muted);
  }
`;

const ExchangeName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text);
  white-space: nowrap;
`;

const ContractType = styled(Badge)`
  background: ${props => (props.$type === 'usdt-m' ? 'var(--accent-tint)' : 'var(--fill)')};
  color: ${props => (props.$type === 'usdt-m' ? 'var(--accent)' : 'var(--text-secondary)')};
`;

const RecordsCount = styled.span`
  display: block;
  margin-top: 4px;
  color: var(--text-tertiary);
  font-size: 0.75rem;
`;

const MetricCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 170px;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const MetricLabel = styled.span`
  color: var(--text-tertiary);
`;

const MetricValue = styled(Rate)`
  font-weight: 500;
`;

const ChartsHeader = styled.div`
  padding: 12px 0 0;
`;

const defaultDailyLocale = {
  eyebrow: 'Daily data',
  title: 'Daily funding snapshot',
  lead: '',
  dateLocale: 'en-US',
  currency: 'Currency',
  date: 'Date',
  dateHint: 'The date is set automatically for the selected currency',
  reload: 'Reload',
  reloading: 'Loading…',
  fetchFresh: 'Fetch fresh data',
  saving: 'Saving…',
  availableDates: 'Available dates',
  moreDates: 'and {count} more',
  schedulerTitle: 'Daily update time (UTC)',
  schedulerLabel: 'Hour : minute',
  hour: 'Hour',
  minute: 'Minute',
  saveTime: 'Save time',
  refresh: 'Refresh',
  loading: 'Loading data…',
  totalExchanges: 'Exchanges in snapshot',
  snapshotTime: 'Snapshot date & time',
  tableTitle: 'Daily funding rates · {currency}',
  exchange: 'Exchange',
  contractType: 'Contract type',
  periods: ['1 day', '30 days', '90 days', '365 days'],
  yesterday: 'yesterday',
  metrics: { avg: 'Avg', sum: 'Sum', min: 'Min', max: 'Max' },
  records: '{count} records',
  chartsTitle: 'Visualization',
  emptyTitle: 'No data to display',
  emptyText: 'Choose a currency and press “Reload” to view the data.',
  loaded: 'Data for {date} ({currency}) loaded',
  notFound: 'No data for {date} ({currency}). Try another date or wait until 15:00.',
  loadError: 'Failed to load data. Please try again later.',
  savedOk: 'Data saved: {file}',
  saveFailed: 'Save failed: {message}',
  saveError: 'Failed to save data. Please try again later.',
  invalidTime: 'Invalid time (hour 0–23, minute 0–59)',
  passwordPrompt: 'Enter the password to confirm changes',
  cancelled: 'Cancelled: no password entered',
  updated: 'Updated: {time}',
  serverError: 'The server returned an error',
  schedulerError: 'Failed to set the scheduler time',
};

const fill = (template, values) =>
  Object.entries(values).reduce((text, [key, value]) => text.replace(`{${key}}`, value), template);

function DailyData({ locale = {} }) {
  const t = { ...defaultDailyLocale, ...locale };
  // Callbacks read copy through a ref so a language switch doesn't re-create them (and refetch).
  const tRef = useRef(t);
  tRef.current = t;
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('BTC');
  const [availableDates, setAvailableDates] = useState([]);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [schedulerHour, setSchedulerHour] = useState('');
  const [schedulerMinute, setSchedulerMinute] = useState('');
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerError, setSchedulerError] = useState(null);
  const [schedulerSuccess, setSchedulerSuccess] = useState(null);

  const loadDailyData = useCallback(async (date, currency = selectedCurrency) => {
    if (!date) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Використовуємо getDailyFundingData з параметрами валюти та дати
      const response = await apiService.getDailyFundingData(currency, date);
      setDailyData(response);
      setSuccess(fill(tRef.current.loaded, { date, currency }));
    } catch (err) {
      console.error('Помилка завантаження даних:', err);
      if (err.response?.status === 404) {
        setError(fill(tRef.current.notFound, { date, currency }));
      } else {
        setError(tRef.current.loadError);
      }
      setDailyData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCurrency]);

  const loadAvailableDates = useCallback(async () => {
    try {
      // Отримуємо доступні дати з сервера для поточної валюти
      const response = await apiService.getAvailableDates(selectedCurrency);
      const dates = response.dates || [];
      setAvailableDates(dates);

      // Автоматично встановлюємо останню доступну дату
      if (dates.length > 0) {
        const latestDate = dates[0]; // Дати вже відсортовані по спаданню
        setSelectedDate(latestDate);
        loadDailyData(latestDate);
      }
    } catch (err) {
      console.error('Помилка завантаження дат:', err);
      // Встановлюємо вчорашню дату як fallback
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const fallbackDate = yesterday.toISOString().split('T')[0];
      setAvailableDates([fallbackDate]);
      setSelectedDate(fallbackDate);
      loadDailyData(fallbackDate);
    }
  }, [selectedCurrency, loadDailyData]);

  const loadSchedulerTime = useCallback(async () => {
    try {
      const resp = await apiService.getSchedulerTime();
      if (resp) {
        setSchedulerHour(String(resp.hour ?? ''));
        setSchedulerMinute(String(resp.minute ?? ''));
      }
    } catch (e) {
      console.error('Помилка отримання часу планувальника', e);
    }
  }, []);

  useEffect(() => {
    loadAvailableDates();
    // Автоматично завантажуємо дані за останню доступну дату залежно від валюти
    loadSchedulerTime();
  }, [loadAvailableDates, loadSchedulerTime]);

  const handleLoadData = () => {
    loadDailyData(selectedDate);
  };

  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    // Дані завантажаться автоматично через useEffect
  };

  const handleTriggerSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.saveDailyData();
      if (response.status === 'success') {
        setSuccess(fill(tRef.current.savedOk, { file: response.file }));
        loadAvailableDates(); // Оновлюємо список дат
        loadDailyData(selectedDate); // Перезавантажуємо поточні дані
      } else {
        setError(fill(tRef.current.saveFailed, { message: response.message }));
      }
    } catch (err) {
      console.error('Помилка збереження даних:', err);
      setError(tRef.current.saveError);
    } finally {
      setLoading(false);
    }
  };

  const handleSetScheduler = async () => {
    setSchedulerLoading(true);
    setSchedulerError(null);
    setSchedulerSuccess(null);
    try {
      const h = parseInt(schedulerHour, 10);
      const m = parseInt(schedulerMinute, 10);
      if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
        setSchedulerError(t.invalidTime);
        return;
      }
      // Попросимо пароль у користувача та відправимо хеш (SHA-256)
      const pwd = window.prompt(t.passwordPrompt);
      if (!pwd) {
        setSchedulerError(t.cancelled);
        return;
      }
      const computeHash = async (text) => {
        const enc = new TextEncoder();
        const data = enc.encode(text);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2,'0')).join('');
      };
      const pwdHash = await computeHash(pwd);
      const resp = await apiService.setSchedulerTime(h, m, pwdHash);
      if (resp.status === 'success' || resp.hour !== undefined) {
        setSchedulerSuccess(fill(t.updated, { time: `${String(resp.hour).padStart(2,'0')}:${String(resp.minute).padStart(2,'0')} ${resp.timezone || 'UTC'}` }));
      } else {
        setSchedulerError(t.serverError);
      }
    } catch (e) {
      console.error('Помилка встановлення часу планувальника', e);
      setSchedulerError(t.schedulerError);
    } finally {
      setSchedulerLoading(false);
    }
  };

const formatRate = (rate, showPercent = true) => {
  if (rate === null || rate === undefined) return '-';
  const percent = (rate * 100).toFixed(6);
  return showPercent ? `${percent}%` : percent;
};

const formatTimestamp = (tsSeconds) => {
  if (!tsSeconds) return '-';
  try {
    const d = new Date(tsSeconds * 1000);
    // Показуємо в UTC у форматі YYYY-MM-DD HH:MM:SS
    const iso = d.toISOString();
    return iso.replace('T', ' ').split('.')[0] + ' UTC';
  } catch (e) {
    return '-';
  }
};

// Header ranges follow the snapshot date: N days back, ending on that date.
const periodRange = (days) => {
  if (!selectedDate) return '';
  const end = new Date(`${selectedDate}T00:00:00Z`);
  if (Number.isNaN(end.getTime())) return '';
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days);
  const format = (date) => date.toLocaleDateString(t.dateLocale, {
    day: '2-digit',
    month: '2-digit',
    ...(days >= 365 && { year: 'numeric' }),
    timeZone: 'UTC',
  });
  return `${format(start)} – ${format(end)}`;
};

const renderMetrics = (periodData, periodName) => {
  const rows = [
    [t.metrics.avg, periodData?.average_funding_rate, true],
    [t.metrics.sum, periodData?.total_funding_rate, false],
    [t.metrics.min, periodData?.min_rate, true],
    [t.metrics.max, periodData?.max_rate, true],
  ];

  return (
    <MetricCell>
      {rows.map(([label, value, showPercent]) => (
        <MetricRow key={label}>
          <MetricLabel>{label}</MetricLabel>
          <MetricValue $tone={periodData ? rateTone(value) : 'neutral'}>
            {periodData ? formatRate(value, showPercent) : '–'}
          </MetricValue>
        </MetricRow>
      ))}
      <RecordsCount>{fill(t.records, { count: periodData?.total_records || 0 })}</RecordsCount>
    </MetricCell>
  );
};

  return (
    <Container>
      <PageHeader>
        <Eyebrow>{locale.eyebrow}</Eyebrow>
        <PageTitle>{locale.title}</PageTitle>
        {locale.lead && <Lead>{locale.lead}</Lead>}
      </PageHeader>

      <Stack>
        <Toolbar>
          <ToolbarRow>
            <Segmented role="group" aria-label={t.currency}>
              {CURRENCIES.map(currency => (
                <SegmentedItem
                  key={currency}
                  type="button"
                  $active={selectedCurrency === currency}
                  aria-pressed={selectedCurrency === currency}
                  onClick={() => handleCurrencyChange(currency)}
                >
                  {currency}
                </SegmentedItem>
              ))}
            </Segmented>

            <ToolbarGroup>
              <DateInput
                type="date"
                value={selectedDate}
                readOnly
                min="2025-12-25"
                max={new Date().toISOString().split('T')[0]}
                title={t.dateHint}
                aria-label={t.date}
              />
              <Button type="button" $variant="secondary" $size="sm" onClick={handleLoadData} disabled={loading}>
                <IconRefresh size={15} strokeWidth={2} />
                {loading ? t.reloading : t.reload}
              </Button>
              <Button type="button" $size="sm" onClick={handleTriggerSave} disabled={loading}>
                {loading ? t.saving : t.fetchFresh}
              </Button>
            </ToolbarGroup>
          </ToolbarRow>

          {availableDates.length > 0 && (
            <AvailableDates>
              {t.availableDates}: {availableDates.slice(0, 10).join(', ')}
              {availableDates.length > 10 && ` ${fill(t.moreDates, { count: availableDates.length - 10 })}`}
            </AvailableDates>
          )}

          <Scheduler>
            <summary>
              <IconChevronRight size={14} strokeWidth={2.25} />
              {t.schedulerTitle}
            </summary>
            <SchedulerRow>
              <SchedulerLabel>{t.schedulerLabel}</SchedulerLabel>
              <TimeInputs>
                <SchedulerInputShort
                  type="number"
                  min="0"
                  max="23"
                  value={schedulerHour}
                  onChange={(e) => setSchedulerHour(e.target.value)}
                  aria-label={t.hour}
                />
                :
                <SchedulerInputShort
                  type="number"
                  min="0"
                  max="59"
                  value={schedulerMinute}
                  onChange={(e) => setSchedulerMinute(e.target.value)}
                  aria-label={t.minute}
                />
              </TimeInputs>
              <Button type="button" $size="sm" onClick={handleSetScheduler} disabled={schedulerLoading}>
                {schedulerLoading ? t.saving : t.saveTime}
              </Button>
              <Button type="button" $variant="secondary" $size="sm" onClick={loadSchedulerTime} disabled={schedulerLoading}>
                {t.refresh}
              </Button>
              {schedulerSuccess && <SchedulerStatus>{schedulerSuccess}</SchedulerStatus>}
              {schedulerError && <SchedulerStatus $tone="error">{schedulerError}</SchedulerStatus>}
            </SchedulerRow>
          </Scheduler>
        </Toolbar>

        {error && <Notice tone="error">{error}</Notice>}
        {success && <Notice tone="success">{success}</Notice>}

        {loading && (
          <Card>
            <Loading>{t.loading}</Loading>
          </Card>
        )}

        {dailyData && (
          <>
            <SummaryGrid>
              <StatCard>
                <StatLabel>{t.totalExchanges}</StatLabel>
                <StatValue>{dailyData.total_exchanges}</StatValue>
              </StatCard>
              <StatCard>
                <StatLabel>{t.snapshotTime}</StatLabel>
                <StatValue>{formatTimestamp(dailyData.timestamp)}</StatValue>
              </StatCard>
            </SummaryGrid>

            <Card $flush>
              <PanelHeader>
                <CardTitle>{fill(t.tableTitle, { currency: selectedCurrency })}</CardTitle>
              </PanelHeader>
              <DataTable>
                <Table>
                  <thead>
                    <tr>
                      <Th scope="col">{t.exchange}</Th>
                      <Th scope="col">{t.contractType}</Th>
                      <Th scope="col">{t.periods[0]}<small>{t.yesterday}</small></Th>
                      <Th scope="col">{t.periods[1]}<small>{periodRange(30)}</small></Th>
                      <Th scope="col">{t.periods[2]}<small>{periodRange(90)}</small></Th>
                      <Th scope="col">{t.periods[3]}<small>{periodRange(365)}</small></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyData.data.map((exchange, index) => (
                      <ExchangeRow key={`${exchange.exchange_id}-${exchange.contract_type}-${exchange.symbol || ''}-${index}`}>
                        <Td>
                          <ExchangeName>{exchange.exchange_name}</ExchangeName>
                        </Td>
                        <Td>
                          <ContractType $type={exchange.contract_type}>
                            {exchange.contract_type.toUpperCase()}
                          </ContractType>
                        </Td>
                        <Td>{renderMetrics(exchange.period_1d, '1d')}</Td>
                        <Td>{renderMetrics(exchange.period_30d, '30d')}</Td>
                        <Td>{renderMetrics(exchange.period_90d, '90d')}</Td>
                        <Td>{renderMetrics(exchange.period_365d, '365d')}</Td>
                      </ExchangeRow>
                    ))}
                  </tbody>
                </Table>
              </DataTable>
            </Card>

            <ChartsHeader>
              <CardTitle as="h2">{t.chartsTitle}</CardTitle>
            </ChartsHeader>
            <FundingCharts data={dailyData} locale={t.charts} />
          </>
        )}

        {!loading && !dailyData && !error && (
          <Card>
            <EmptyState>
              <h3>{t.emptyTitle}</h3>
              <p>{t.emptyText}</p>
            </EmptyState>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

export default DailyData;
